import React, { useEffect, useRef, useState } from "react";
import {
  InitialSetupContainer,
  Input,
  InputWrapper,
  Label,
  MediumInputWrapper,
  SampleWrapper,
  SmallInputWrapper,
  TextArea,
  Title,
  Symbol,
  PercentInput,
  WarningMessage,
  Counter,
  InputTitle,
  ImageUploadWrapper,
  ImageUploadSection,
  RenderedImage,
  ImageInputWrapper,
  ReportErrorMessage,
  BtnWrapper,
} from "./style";
import { useReport } from "src/sections/ReportManager/context/ReportContext";
import { ALLOWED_FILE_TYPES, DEFAULT_COVER_IMAGE, ERROR_FILE_MESSAGES, formConfig, IMAGE_LABEL, MAX_FILE_SIZE_BYTE, MAX_FILE_SIZE_MB, TIPOS_ERROS } from "src/sections/ReportManager/constants";
import { slicePageTitle } from "src/sections/ReportManager/utils/slicePageTitle";
import { recuperarPrimeiroValorComComprimento } from "src/util/string/primeiroValorComComprimento";
import { Button } from "src/components";
import { reportManagerService } from "src/sections/ReportManager/service";

export const InitialSetup = () => {
  const { initialSetup, reportConfig, setReportConfig } = useReport();
  const [warning, setWarning] = useState({});
  const [warningTitle, setWarningTitle] = useState(false);
  const isFirstLoad = useRef(true); // Flag para controlar a inicialização
  const [imageLabel, setImageLabel] = useState(IMAGE_LABEL.UPLOAD_INSTRUCTION);
  const [imageErrors, setImageErrors] = useState({
    sizeExceeded: false, // quando usuario seleciona um arquivo maior que o permitido
    apiError: false // quando ocorre erro ao persistir a imagem na aws
  })

  const fileInputRef = useRef(null);

  // Inicializa os valores no contexto apenas na primeira montagem
  useEffect(() => {
    if (isFirstLoad.current) {
      if (initialSetup?.titulo && initialSetup?.titulo.length > 80) {
        setWarningTitle(true);
      }
      setReportConfig((prevConfig) => ({
        ...prevConfig,
        pageTitle: slicePageTitle(recuperarPrimeiroValorComComprimento("", prevConfig.pageTitle, initialSetup?.titulo)),
        clientName: recuperarPrimeiroValorComComprimento("", prevConfig.clientName, initialSetup?.cliente_nome),
        sample: recuperarPrimeiroValorComComprimento("", prevConfig.sample, initialSetup?.entrevistas?.toString()),
      }));
      
      isFirstLoad.current = false; // Marca como inicializado
    }
  }, [initialSetup, setReportConfig]);

  const warningControl = (key, value) => {
    let warningState = false;
    if (value > 100) {
      warningState = true;
    } else {
      warningState = false;
    }
    setWarning((prevWarning) => ({
      ...prevWarning,
      [key]: warningState,
    }));
  };

  // controla o warning do input de Margem de erro
  useEffect(() => {
    warningControl("error_margin", reportConfig.error_margin);
  }, [reportConfig.error_margin]);

  // controla o warning do input de Nível de confiança
  useEffect(() => {
    warningControl("trust", reportConfig.trust);
  }, [reportConfig.trust]);

  const handleInputChange = (event, key, onlyNumbers = false) => {
    let { value } = event.target;

    // Limpa o warning mediante a edição do usuário
    if(key === 'pageTitle' && warningTitle === true) {
      setWarningTitle(false);
    }

    if (onlyNumbers && value) {
      value = value.replace(/[^0-9]/g, ""); // Remove caracteres não numéricos
    }

    setReportConfig((prevConfig) => ({
      ...prevConfig,
      [key]: value,
    }));
  };

  /**
   * Remove a imagem de capa atual da referência e define a imagem padrão no state
   */
  const handleRemoveImage = () => {
    setReportConfig(prev => ({
      ...prev,
      coverImageUrl: null,
    }));
    fileInputRef.current.value = "";
  }

  /**
   * Valida a imagem selecionada pelo usuário
   */
  const handleValidateImage = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE_BYTE) {
        updateErrorState(TIPOS_ERROS.SIZE_EXCEEDED, true);
        handleRemoveImage();
        return;
      }
      updateErrorState(TIPOS_ERROS.SIZE_EXCEEDED, false);
      processAndUploadImage(file);
    }
  }

  /**
   * Processa e faz o upload da imagem selecionada pelo usuário
   * Se ocorrer sucesso, atualiza o estado com o URL da imagem
   * Se ocorrer erro, define a imagem padrão e seta o erro de API
   */
  const processAndUploadImage = async (file) => {
    setImageLabel(IMAGE_LABEL.LOADING_MESSAGE);
    try {
      const response = await reportManagerService.persistReportCoverImage(file);
      setReportConfig(prev => ({
        ...prev,
        coverImageUrl: response.data,
      }));

      setImageLabel(IMAGE_LABEL.UPLOAD_INSTRUCTION);
      updateErrorState(TIPOS_ERROS.API_ERROR, false);

    } catch (error) {
      handleRemoveImage();
      setImageLabel(IMAGE_LABEL.UPLOAD_INSTRUCTION);
      updateErrorState(TIPOS_ERROS.API_ERROR, true);
    }
  }

  // Atualiza o state de erro
  const updateErrorState = (errorKey, value) => {
    setImageErrors(prev => ({
      ...prev,
      [errorKey]: value,
    }));
  };

  return (
    <InitialSetupContainer>
      <Title>Identidade do relatório</Title>
      <ImageUploadSection>
        <Label>Envie uma imagem personalizada para o relatório. Se você não enviar, o sistema usará a imagem padrão do Tensai.</Label>
        <ImageUploadWrapper hasError={imageErrors.sizeExceeded || imageErrors.apiError}>

          <RenderedImage src={reportConfig.coverImageUrl || DEFAULT_COVER_IMAGE} alt="imagem renderizada" />

          <ImageInputWrapper>
            <Title>Insira uma imagem para capa</Title>
            <Label>{imageLabel}</Label>
            <input type="file" accept={`.${ALLOWED_FILE_TYPES.PNG}, .${ALLOWED_FILE_TYPES.JPG}`} hidden ref={fileInputRef} onChange={(event) => handleValidateImage(event)} />
            
            { !reportConfig.coverImageUrl
              ? <Button
                  type="button"
                  title="Carregar imagem"
                  onClick={() => fileInputRef.current?.click()}
                  className="image-upload-button"
                />
                
              : <BtnWrapper>
                    <Button
                      type="button"
                      title="Excluir"
                      clear={true}
                      onClick={() => handleRemoveImage(DEFAULT_COVER_IMAGE)}
                      className="uploaded-btn clear-btn"
                    />

                    <Button
                      type="button"
                      title="Editar"
                      onClick={() => fileInputRef.current?.click()}
                      className="uploaded-btn"
                    />
                </BtnWrapper>
            }

          </ImageInputWrapper>
          {imageErrors.sizeExceeded && <ReportErrorMessage>
            { ERROR_FILE_MESSAGES.SIZE_EXCEEDED }
          </ReportErrorMessage> }

          {imageErrors.apiError && <ReportErrorMessage>
            { ERROR_FILE_MESSAGES.API_ERROR }
          </ReportErrorMessage> }

        </ImageUploadWrapper>
      </ImageUploadSection>
      <Title>Capa</Title>
      <InputWrapper>
        <Label>Título do relatório*</Label>
        <InputTitle
          maxLength={formConfig.reporterTitlteMaxLength}
          placeholder={slicePageTitle(initialSetup?.titulo) || ""}
          onChange={(event) => handleInputChange(event, "pageTitle")}
          value={reportConfig?.pageTitle || ""}
          warning={warningTitle}
          required
        />
        <Counter>
          {reportConfig?.pageTitle?.length || 0} /{" "}
          {formConfig.reporterTitlteMaxLength}
        </Counter>
        {warningTitle && (
          <WarningMessage>Limite de caracteres excedido. Utilize um título mais curto</WarningMessage>
        )}
      </InputWrapper>
      <InputWrapper>
        <Label>Nome do cliente</Label>
        <Input
          maxLength={formConfig.clientNameMaxLength}
          placeholder="nome_do_cliente"
          onChange={(event) => handleInputChange(event, "clientName")}
          value={reportConfig?.clientName || ""}
        />
        <Counter>
          {reportConfig?.clientName?.length || 0} /{" "}
          {formConfig.clientNameMaxLength}
        </Counter>
      </InputWrapper>

      <Title>Amostra</Title>
      <SampleWrapper>
        <SmallInputWrapper>
          <Label>Entrevistas</Label>
          <Input
            maxLength={formConfig.sampleMaxLength}
            placeholder="200"
            onChange={(event) => handleInputChange(event, "sample", true)}
            value={reportConfig?.sample || ""}
          />
          <Counter>
            {reportConfig?.sample?.toString().length || 0} /{" "}
            {formConfig.sampleMaxLength}
          </Counter>
        </SmallInputWrapper>
        <SmallInputWrapper>
          <Label>Margem de erro</Label>
          <PercentInput
            placeholder="0%"
            onChange={(event) => handleInputChange(event, "error_margin", true)}
            value={reportConfig?.error_margin || ""}
            warning={warning?.error_margin}
          />
          <Symbol>%</Symbol>
          {warning.error_margin && (
            <WarningMessage>Valor superior a 100</WarningMessage>
          )}
        </SmallInputWrapper>
        <SmallInputWrapper>
          <Label>Nível de confiança</Label>
          <PercentInput
            placeholder="0%"
            onChange={(event) => handleInputChange(event, "trust", true)}
            value={reportConfig?.trust || ""}
            warning={warning?.trust}
          />
          <Symbol>%</Symbol>
          {warning.trust && (
            <WarningMessage>Valor superior a 100</WarningMessage>
          )}
        </SmallInputWrapper>
        <MediumInputWrapper>
          <Label>Período de campo</Label>
          <Input
            maxLength={formConfig.fieldPeriodMaxLength}
            placeholder="Dia 01/01 a 05/02 de 2024"
            onChange={(event) => handleInputChange(event, "field_period")}
            value={reportConfig?.field_period || ""}
          />
          <Counter>
            {reportConfig?.field_period?.length || 0} /{" "}
            {formConfig.fieldPeriodMaxLength}
          </Counter>
        </MediumInputWrapper>
      </SampleWrapper>

      <Title>Metodologia</Title>
      <InputWrapper>
        <Label>Metodologia da pesquisa</Label>
        <TextArea
          maxLength={formConfig.textAreaMaxLength}
          placeholder="Escreva aqui a metodologia da pesquisa"
          onChange={(event) => handleInputChange(event, "method")}
          value={reportConfig?.method || ""}
        />
        <Counter>
          {reportConfig?.method?.length || 0} / {formConfig.textAreaMaxLength}
        </Counter>
      </InputWrapper>

      <Title>Conclusões estratégicas</Title>
      <InputWrapper>
        <Label>Resultados da avaliação</Label>
        <TextArea
          maxLength={formConfig.textAreaMaxLength}
          placeholder="Descreva as principais conclusões e insights estratégicos mais relevantes da avaliação"
          onChange={(event) => handleInputChange(event, "considerations")}
          value={reportConfig?.considerations || ""}
        />
        <Counter>
          {reportConfig?.considerations?.length || 0} /{" "}
          {formConfig.textAreaMaxLength}
        </Counter>
      </InputWrapper>
    </InitialSetupContainer>
  );
};
